import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent, Optional} from 'util/Types';

export interface ExtraActionButtons {
  viewMode: Array<JSX.Element>;
  editMode: Array<JSX.Element>;
}

interface BaseProps {
  archive?: Optional<() => void>;
  cancel?: Optional<() => void>;
  complete?: Optional<() => void | Promise<void>>;
  copyAPIID?: Optional<() => void | Promise<void>>;
  edit?: Optional<(e: ElementMouseEvent) => void | Promise<void>>;
  remove?: Optional<(e?: ElementMouseEvent) => void>;
  unarchive?: Optional<() => void>;
  className: string;
  isCompact?: boolean;
  isCompleteEnabled?: boolean;
  isEditing: boolean;
  extraActionButtons?: ExtraActionButtons;
  disableArchiveButtonWithTooltip?: string | null;
  disableDeleteButtonWithTooltip?: string | null;
}

export interface FieldActionButtonsProps extends BaseProps {
  isEditing: boolean;
  cancel?: () => void;
  complete?: () => void | Promise<void>;
  isCompleteEnabled?: boolean;
  isCancelEnabled?: boolean;
  hasOuterMargin?: boolean;
  isArchiveEnabled?: boolean;
  isUnarchiveEnabled?: boolean;
}

function ViewModeFieldActionButtons(props: FieldActionButtonsProps): JSX.Element {
  const renderDeleteIcon = (remove: () => void): JSX.Element => {
    if (props.disableDeleteButtonWithTooltip != null) {
      return (
        <Tooltip
          container="body"
          placement={TooltipPlacement.TOP}
          title={props.disableDeleteButtonWithTooltip}
        >
          <span
            className={classNames(css(styles.singleButton), 'metadata-action faded')}
            {...getTestProps({component: 'FieldActionButtons', element: 'delete'})}
          >
            <BenchlingIcon icon="delete" />
          </span>
        </Tooltip>
      );
    } else {
      return (
        <a
          className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer')}
          {...getTestProps({component: 'FieldActionButtons', element: 'delete'})}
          onClick={remove}
        >
          <BenchlingIcon icon="delete" />
        </a>
      );
    }
  };

  const showAPIID = usePreference('ENABLE_COPY_API_ID')[0];

  return (
    <span className={css(styles.buttonsWrapper)}>
      {props.copyAPIID && showAPIID && (
        <Tooltip title="Copy API ID" container="body" placement={TooltipPlacement.TOP}>
          <span
            className={classNames(css(styles.singleButton), 'metadata-action faded')}
            onClick={props.copyAPIID}
            {...getTestProps({component: 'FieldActionButtons', element: 'copy-api-id'})}
          >
            <BenchlingIcon icon="note-code" />
          </span>
        </Tooltip>
      )}
      {props.remove && renderDeleteIcon(props.remove)}
      {(() => {
        if (BenchlingConfig.ENABLE_ARCHIVING_V2 && props.archive) {
          if (props.disableArchiveButtonWithTooltip != null) {
            return (
              <Tooltip
                container="body"
                placement={TooltipPlacement.TOP}
                title={props.disableArchiveButtonWithTooltip}
              >
                <span
                  className={classNames(css(styles.singleButton), 'metadata-action faded')}
                  {...getTestProps({component: 'FieldActionButtons', element: 'archive'})}
                >
                  <BenchlingIcon icon="archive" />
                </span>
              </Tooltip>
            );
          } else {
            return (
              <Tooltip container="body" placement={TooltipPlacement.TOP} title="Archive">
                <a
                  className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer', {
                    inactive: !(props.isArchiveEnabled ?? true),
                  })}
                  onClick={props.archive}
                  {...getTestProps({component: 'FieldActionButtons', element: 'archive'})}
                >
                  <BenchlingIcon icon="archive" />
                </a>
              </Tooltip>
            );
          }
        } else if (BenchlingConfig.ENABLE_ARCHIVING_V2 && props.unarchive) {
          return (
            <Tooltip container="body" placement={TooltipPlacement.TOP} title="Unarchive">
              <a
                className={classNames(css(styles.singleButton), 'metadata-action faded u-primary-pointer', {
                  inactive: !(props.isUnarchiveEnabled ?? true),
                })}
                onClick={props.unarchive}
                {...getTestProps({component: 'FieldActionButtons', element: 'unarchive'})}
              >
                <BenchlingIcon icon="unarchive" />
              </a>
            </Tooltip>
          );
        }
        return null;
      })()}
      {props.edit != null && (
        <a
          className={classNames(css(styles.singleButton), 'metadata-action faded u-primary-pointer')}
          onClick={props.edit}
          {...getTestProps({component: 'FieldActionButtons', element: 'edit'})}
        >
          <BenchlingIcon icon="edit" />
        </a>
      )}
      {props.extraActionButtons && props.extraActionButtons.viewMode}
    </span>
  );
}

function EditModeFieldActionButtons(props: FieldActionButtonsProps): JSX.Element {
  return (
    <span className={css(styles.buttonsWrapper)}>
      <a
        className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer', {
          inactive: !(props.isCancelEnabled ?? true),
        })}
        onClick={props.cancel}
        {...getTestProps({component: 'FieldActionButtons', element: 'cancel'})}
      >
        <BenchlingIcon className="metadata-action-icon--close" icon="close" />
      </a>
      {props.extraActionButtons && props.extraActionButtons.editMode}
      <a
        className={classNames(css(styles.singleButton), 'metadata-action u-primary-pointer', {
          inactive: !props.isCompleteEnabled,
        })}
        onClick={props.complete}
        {...getTestProps({component: 'FieldActionButtons', element: 'complete'})}
      >
        <BenchlingIcon
          className={classNames('metadata-action-icon--complete', {
            inactive: !props.isCompleteEnabled,
          })}
          icon="check"
        />
      </a>
    </span>
  );
}

function FieldActionButtons(props: FieldActionButtonsProps): JSX.Element {
  return (
    <div
      className={classNames(props.hasOuterMargin && css(styles.outerMargin), props.className, {
        isCompact: props.isCompact,
      })}
      {...getTestProps({component: 'FieldActionButtons', element: 'root'})}
    >
      {props.isEditing ? (
        <EditModeFieldActionButtons {...props} />
      ) : (
        <ViewModeFieldActionButtons {...props} />
      )}
    </div>
  );
}

FieldActionButtons.defaultProps = {
  className: 'registry-actionGroup',
};

type Args = [string, string, string, string];

FieldActionButtons.propTypes = {
  archive: PropTypes.func,
  cancel: (props: FieldActionButtonsProps, ...args: Args) => {
    if (props.isEditing) {
      return PropTypes.func.isRequired(props, ...args);
    }
    return PropTypes.func(props, ...args);
  },
  className: PropTypes.string.isRequired,
  complete: (props: FieldActionButtonsProps, ...args: Args) => {
    if (props.isEditing) {
      return PropTypes.func.isRequired(props, ...args);
    }
    return PropTypes.func(props, ...args);
  },
  edit: PropTypes.func,
  isCompact: PropTypes.bool,
  isCompleteEnabled: (props: FieldActionButtonsProps, ...args: Args) => {
    if (props.isEditing) {
      return PropTypes.bool.isRequired(props, ...args);
    }
    return PropTypes.bool(props, ...args);
  },
  isEditing: PropTypes.bool.isRequired,
  remove: PropTypes.func,
  unarchive: PropTypes.func,
};

export default FieldActionButtons;

export const styles = StyleSheet.create({
  singleButton: {
    marginLeft: '6px',
    marginRight: '6px',
    padding: '0px',
    display: 'flex',
    alignItems: 'center',
  },
  outerMargin: {marginRight: '8px'},
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});
